<template>
  <div class="otp-dashboard-module">
    <div class="d-flex flex-wrap justify-content-between gap-3 p-3">
      <div class="d-flex align-items-center">
        <h6 class="dashboard-tile-title mb-0">{{ $t('flexigo.otp.otpPerformance') }}</h6>
      </div>
      <div class="d-flex justify-content-end flex-row gap-3 flex-wrap align-items-center">
        <otp-threshold-slider
          :threshold="thresholdValue"
          :label="$t('flexigo.otp.lateArrivalThreshold')"
          @ready="handleThresholdReady"
          @change="handleThresholdChange"
        />
        <date-range-filter
          :selected-date="filters.dateRange"
          default-selection="lastSevenDays"
          :last-date="new Date()"
          @change="handleDateChange"
        />
      </div>
    </div>

    <!-- Filters Section -->
    <div class="p-3 border-bottom border-top">
      <div class="hstack gap-3 flex-wrap">
        <toggle-button-group v-if="shouldShowToggle" :options="workingTypeOptions" @change="onWorkingTypeChange" />
        <otp-direction-filter
          :selected-direction="filters.direction"
          :selected-working-type="selectedWorkingType"
          @change="handleDirectionChange"
        />
        <otp-route-filter
          ref="routeFilter"
          :selected-routes="filters.routeIds"
          :preset-route-ids="presetRouteIds"
          :no-routes="noPresetRoutes"
          :selected-direction="filters.direction"
          :selected-working-type="selectedWorkingType"
          @change="handleRouteChange"
          @preset-applied="handlePresetApplied"
        />
        <otp-driver-filter :selected-drivers="filters.driverIds" @change="handleDriverChange" />
        <otp-stop-type-filter :selected-type="filters.stopType" @change="handleStopTypeChange" />
      </div>
    </div>

    <!-- Tabs Navigation -->
    <div class="otp-tabs-container">
      <ul class="nav nav-tabs px-3">
        <li class="nav-item">
          <a href="#" class="nav-link" :class="{ active: activeTab === 'date' }" @click.prevent="activeTab = 'date'">
            {{ $t('flexigo.otp.byDate') }}
          </a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link" :class="{ active: activeTab === 'route' }" @click.prevent="activeTab = 'route'">
            {{ $t('flexigo.otp.byRoute') }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Tab Content -->
    <div class="otp-content-container">
      <otp-by-date
        v-if="activeTab === 'date'"
        :filters="filters"
        :threshold="thresholdValue"
        :loading="loading"
        @drill-down="handleDrillDown"
      />
      <otp-by-route
        v-if="activeTab === 'route'"
        :filters="filters"
        :threshold="thresholdValue"
        :loading="loading"
        @route-selected="handleRouteSelected"
        @drill-down="handleDrillDown"
      />
      <otp-by-stops
        v-if="activeTab === 'stops'"
        :filters="filters"
        :selected-route-id="selectedRouteId"
        :loading="loading"
        @back="handleBackToRoutes"
        @drill-down="handleDrillDown"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { debounce } from '@/helpers/utility';
import DateRangeFilter from '../../dateRangeFilter.vue';
import OtpRouteFilter from './filters/OtpRouteFilter.vue';
import OtpDriverFilter from './filters/OtpDriverFilter.vue';
import OtpDirectionFilter from './filters/OtpDirectionFilter.vue';
import OtpStopTypeFilter from './filters/OtpStopTypeFilter.vue';
import OtpThresholdSlider from './filters/OtpThresholdSlider.vue';
import OtpByDate from './views/OtpByDate.vue';
import OtpByRoute from './views/OtpByRoute.vue';
import OtpByStops from './views/OtpByStops.vue';
import toggleButtonGroup from '../../toggleButton/toggleButtonGroup.vue';
import otpDataMixin from './mixins/otpDataMixin';
import otpDrillDownMixin from './mixins/otpDrillDownMixin';
import './styles/otpTooltip.css';

export default {
  name: 'OtpDashboardModule',
  components: {
    DateRangeFilter,
    OtpRouteFilter,
    OtpDriverFilter,
    OtpDirectionFilter,
    OtpStopTypeFilter,
    OtpThresholdSlider,
    OtpByDate,
    OtpByRoute,
    OtpByStops,
    toggleButtonGroup,
  },
  mixins: [otpDataMixin, otpDrillDownMixin],
  props: {
    // Route ids to preselect in the route filter, e.g. the routes of the route groups picked on the
    // dashboard. null leaves the filter on every route. The user can still change it afterwards.
    presetRouteIds: {
      type: Array,
      default: null,
    },
    // Date range to preset on the date filter, e.g. the dashboard header's, in date-range-filter's
    // shape. null keeps the store's range. The user can still change it afterwards.
    presetDateRange: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedWorkingType: 'All',
      shouldShowToggle: false,
      activeTab: 'date',
      selectedRouteId: null,
      thresholdValue: 5,
      // The first OTP request waits for the threshold slider to resolve its initial
      // value (user preference -> company setting -> default), otherwise the dashboard
      // fires one request with the store default and a second one with the real value.
      thresholdReady: false,
      // With a route preset, OTP requests wait until the route filter has applied it: earlier they'd go
      // out with the store's previous selection, and their response would narrow the working type.
      presetResolved: !this.presetRouteIds,
      // An empty preset: the picked route groups have no routes in the range. OTP then shows no data (and
      // sends no request) until the user picks routes; an empty route selection would mean every route.
      noPresetRoutes: Array.isArray(this.presetRouteIds) && this.presetRouteIds.length === 0,
      debouncedOtpFetch: null,
    };
  },
  computed: {
    ...mapGetters('otpDashboard', {
      routeStats: 'routeStats',
      dateStats: 'dateStats',
      loading: 'loading',
      error: 'error',
      hasData: 'hasData',
      filters: 'filters',
      rawTripData: 'rawTripData',
    }),
    workingTypeOptions() {
      return [
        {
          label: this.$t('flexigo.common.all'),
          selected: this.selectedWorkingType === 'All',
        },
        {
          label: this.$t('flexigo.workgroup.fixed'),
          selected: this.selectedWorkingType === 'Fixed',
        },
        {
          label: this.$t('flexigo.workgroup.dynamic'),
          selected: this.selectedWorkingType === 'Dynamic',
        },
      ];
    },
  },
  watch: {
    filters: {
      handler() {
        // If on stops view, go back to route view when filters change
        if (this.activeTab === 'stops') {
          this.handleBackToRoutes();
        }
      },
      deep: true,
    },
    presetDateRange() {
      this.applyPresetDateRange();
    },
    presetRouteIds(routeIds) {
      this.presetResolved = !routeIds;
      this.noPresetRoutes = Array.isArray(routeIds) && routeIds.length === 0;
    },
    'filters.dateRange': {
      handler() {
        this.fetchRoutesAndDriversData();
        this.requestOtpFetch();
      },
    },
    'filters.threshold': {
      handler() {
        this.requestOtpFetch();
      },
    },
    'filters.routeIds': {
      handler() {
        this.requestOtpFetch();
      },
    },
    'filters.driverIds': {
      handler() {
        this.requestOtpFetch();
      },
    },
    'filters.stopType': {
      handler() {
        // StopType changes don't require new API call, just re-aggregate existing data
        this.reAggregateData();
      },
    },
    'filters.direction': {
      handler() {
        // Direction changes don't require new API call, just re-aggregate existing data
        this.reAggregateData();
      },
    },
    selectedWorkingType(newType) {
      // Clear selected routes if they don't match the new working type
      if (this.filters.routeIds.length > 0 && newType !== 'All') {
        const targetType = newType.toUpperCase(); // 'Dynamic' -> 'DYNAMIC', 'Fixed' -> 'FIXED'
        const storeRoutes = this.$store.getters['otpDashboard/availableRoutes'] || [];
        const hasIncompatibleRoutes = this.filters.routeIds.some((selectedRoute) => {
          const ids = Array.isArray(selectedRoute.id) ? selectedRoute.id : [selectedRoute.id];
          // Grouped selections made under "All" can mix FIXED and DYNAMIC
          // routes; the group's own workingType only describes its first route,
          // so a single-id fast path is only safe for non-grouped selections.
          if (ids.length === 1 && selectedRoute.workingType) {
            return selectedRoute.workingType !== targetType;
          }
          return ids.some((id) => {
            const match = storeRoutes.find((route) => route.id === id);
            return match && match.workingType !== targetType;
          });
        });

        if (hasIncompatibleRoutes) {
          this.$store.commit('otpDashboard/UPDATE_ROUTES', []);
        }
      }

      // Dynamic routes do not support LOOP, so normalize the direction before fetching.
      if (newType === 'Dynamic' && this.filters.direction === 'loop') {
        this.$store.commit('otpDashboard/UPDATE_DIRECTION', 'all');
      }
      // Narrowed to the only working type in the data just fetched: that data already is this type's.
      const { narrowedToData } = this;
      this.narrowedToData = false;
      if (!narrowedToData) this.requestOtpFetch();
    },
    rawTripData: {
      immediate: true,
      handler(newTrips) {
        // Set shouldShowToggle based on workingType in OTP data
        if (newTrips && newTrips.length > 0 && !this.shouldShowToggle) {
          const hasFixed = newTrips.some((trip) => trip.workingType === 'FIXED');
          const hasDynamic = newTrips.some((trip) => trip.workingType === 'DYNAMIC');
          this.shouldShowToggle = hasFixed && hasDynamic;

          // Auto-set selectedWorkingType based on available data
          let dataType = null;
          if (!hasFixed && hasDynamic) dataType = 'Dynamic';
          else if (hasFixed && !hasDynamic) dataType = 'Fixed';
          // Only a change runs the selectedWorkingType watcher, which clears the flag.
          if (dataType && dataType !== this.selectedWorkingType) {
            this.narrowedToData = true;
            this.selectedWorkingType = dataType;
          }
        }
      },
    },
  },
  // Same as the Ridership module: the immediate rawTripData watcher would narrow the working type (and
  // drop the preset routes that don't match it) from the previous view's data.
  beforeCreate() {
    this.$store.commit('otpDashboard/CLEAR_DATA');
  },
  created() {
    // A single user action often lands several filter mutations: changing direction also
    // resets routes and drivers, and UPDATE_ROUTES/UPDATE_DRIVERS always assign a fresh
    // array so their watchers fire even when the selection did not change. Dragging the
    // threshold slider produces a burst of its own. Debouncing collapses each of those
    // into one request instead of one request per watcher hit.
    this.debouncedOtpFetch = debounce(this.fetchOtpDashboardData, 250);
    // Applied before mount so the first requests already carry the preset range. The filters
    // live in the store and outlast this component, so they may hold an older range.
    this.presetDateRangeApplied = this.applyPresetDateRange();
  },
  async mounted() {
    // The initial OTP request is triggered by handleThresholdReady, once the slider has
    // resolved the threshold the request has to carry. A preset applied in created() has
    // already set the filters.dateRange watcher off, which fetches the routes itself.
    if (!this.presetDateRangeApplied) await this.fetchRoutesAndDriversData();
  },
  // The debounce helper can't be cancelled, so a call queued before the dashboard was rebuilt still
  // fires afterwards; it must not dispatch a request with this module's (old) working type.
  beforeDestroy() {
    this.disposed = true;
  },
  methods: {
    ...mapActions('otpDashboard', ['fetchOtpData', 'fetchRoutesAndDrivers', 'clearData', 'reAggregateData']),
    handleDateChange(dateRange) {
      this.$store.commit('otpDashboard/UPDATE_DATE_RANGE', dateRange);
    },
    // Returns whether the store's range changed (and so the filters.dateRange watcher will refetch).
    applyPresetDateRange() {
      const preset = this.presetDateRange;
      const current = this.filters.dateRange;
      if (!preset) return false;
      if (preset.type === current?.type && preset.startDay === current?.startDay && preset.endDay === current?.endDay) {
        return false;
      }
      this.$store.commit('otpDashboard/UPDATE_DATE_RANGE', { ...preset });
      return true;
    },
    handleRouteChange(routes) {
      // Picking routes ends the "no routes" state; clearing them afterwards means every route.
      if (routes?.length) this.noPresetRoutes = false;
      this.$store.commit('otpDashboard/UPDATE_ROUTES', routes);
    },
    handleDriverChange(drivers) {
      this.$store.commit('otpDashboard/UPDATE_DRIVERS', drivers);
    },
    handleDirectionChange(direction) {
      this.$store.commit('otpDashboard/UPDATE_DIRECTION', direction);

      // 'all' covers every direction — keep the current selection untouched.
      if (direction === 'all' || this.filters.routeIds.length === 0) return;

      // Keep only selections compatible with the new direction. A grouped
      // selection (id array) survives only if every route it expands to matches.
      const dirLower = direction.toLowerCase();
      const storeRoutes = this.$store.getters['otpDashboard/availableRoutes'] || [];
      const compatibleRoutes = this.filters.routeIds.filter((selectedRoute) => {
        const ids = Array.isArray(selectedRoute.id) ? selectedRoute.id : [selectedRoute.id];
        return ids.every((id) => {
          const route = storeRoutes.find((r) => r.id === id) || selectedRoute;
          return route.direction && route.direction.toLowerCase() === dirLower;
        });
      });

      if (compatibleRoutes.length !== this.filters.routeIds.length) {
        this.$store.commit('otpDashboard/UPDATE_ROUTES', compatibleRoutes);
        this.$store.commit('otpDashboard/UPDATE_DRIVERS', []);
      }
    },
    handleStopTypeChange(stopType) {
      this.$store.commit('otpDashboard/UPDATE_STOP_TYPE', stopType);
    },
    onWorkingTypeChange({ index }) {
      const types = ['All', 'Fixed', 'Dynamic'];
      this.selectedWorkingType = types[index];
    },
    handleRouteSelected(routeId) {
      this.selectedRouteId = routeId;
      this.activeTab = 'stops';
    },
    handleBackToRoutes() {
      this.activeTab = 'route';
      this.selectedRouteId = null;
    },
    handleDrillDown(params) {
      this.navigateToTrips(params);
    },
    handleThresholdChange(value) {
      this.thresholdValue = value;
      this.$store.commit('otpDashboard/UPDATE_THRESHOLD', value);
    },
    handleThresholdReady(value) {
      this.thresholdValue = value;
      this.thresholdReady = true;
      // Commit first: when the resolved threshold differs from the store default this
      // schedules the fetch through the watcher, and requestOtpFetch covers the case
      // where it matches and no watcher fires. Both land on the same debounced call.
      this.$store.commit('otpDashboard/UPDATE_THRESHOLD', value);
      this.requestOtpFetch();
    },
    requestOtpFetch() {
      // Filter changes made before the threshold resolves need no request of their own:
      // handleThresholdReady fetches with whatever filters are current by then. Likewise for a
      // route preset still being applied: handlePresetApplied fetches once it is.
      if (!this.thresholdReady || !this.presetResolved || this.disposed) return;
      this.debouncedOtpFetch();
    },
    handlePresetApplied() {
      this.presetResolved = true;
      this.requestOtpFetch();
    },

    async fetchRoutesAndDriversData() {
      try {
        // Get dates from dateRange object - now includes startDay/endDay from filter
        const { startDay, endDay } = this.filters.dateRange || {};

        if (!startDay || !endDay) {
          console.warn('Date range not properly set');
          return;
        }

        // Dispatch Vuex action to fetch routes and drivers
        await this.fetchRoutesAndDrivers({ startDay, endDay });
      } catch (error) {
        console.error('Error fetching routes and drivers:', error);
        // Without a route list the filter can't match the preset to routes. It filters by the preset ids as
        // they are instead (and emits preset-applied), rather than leave OTP waiting.
        if (!this.presetResolved && !this.disposed) this.$refs.routeFilter.applyPresetFallback();
      }
    },

    async fetchOtpDashboardData() {
      // Reached through debouncedOtpFetch, which may fire after this module was destroyed.
      if (this.disposed) return;
      if (this.noPresetRoutes && !this.filters.routeIds?.length) {
        this.clearData();
        return;
      }
      try {
        // Get dates from dateRange object - now includes startDay/endDay from filter
        const { startDay, endDay } = this.filters.dateRange || {};
        const { routeIds, driverIds, threshold } = this.filters;

        if (!startDay || !endDay) {
          console.warn('Date range not properly set');
          return;
        }

        const params = {
          startDay,
          endDay,
          threshold: threshold || this.thresholdValue,
        };

        // Extract IDs from route objects if they exist
        // Handle ring groups where route.id might be an array of IDs
        if (routeIds && routeIds.length > 0 && !routeIds.includes('all')) {
          params.routeIds = routeIds.flatMap((route) => {
            if (typeof route === 'object') {
              // If route.id is an array (ring group), return all IDs
              if (Array.isArray(route.id)) {
                return route.id;
              }
              // Otherwise return single ID
              return route.id;
            }
            return route;
          });
        }

        // Extract IDs from driver objects if they exist
        if (driverIds && driverIds.length > 0 && !driverIds.includes('all')) {
          params.driverIds = driverIds.map((driver) => (typeof driver === 'object' ? driver.id : driver));
        }

        // Map selectedWorkingType to backend workingTypes parameter
        if (this.selectedWorkingType === 'Fixed') {
          params.workingTypes = 'FIXED';
        } else if (this.selectedWorkingType === 'Dynamic') {
          params.workingTypes = 'DYNAMIC';
        } else {
          params.workingTypes = 'DYNAMIC,FIXED';
        }

        // Dispatch Vuex action to fetch and aggregate data
        await this.fetchOtpData(params);
      } catch (error) {
        console.error('Error fetching OTP dashboard data:', error);
      }
    },
  },
};
</script>

<style scoped>
.otp-dashboard-module {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.otp-header {
  margin-bottom: 20px;
}

.otp-filters-container {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.otp-content-container {
  min-height: 600px;
}
</style>
